import {
  BASKET_CAP, GRANARY_CAP, GRANARY_COVERAGE_RADIUS,
  SeedType, SimState, Tile,
} from './types';
import { PlotSystem } from './PlotSystem';
import { ConstructionSystem } from './ConstructionSystem';

export interface StorageReport {
  basketUsed: number;
  basketCap: number;
  granaryUsed: number;
  granaryCap: number;
  totalUsed: number;
  totalCap: number;
  byCrop: Record<SeedType, number>;
}

/**
 * Storage on the plot: a small "basket" capacity + N granaries × cap.
 * A harvest from tile (c, r) prefers a granary within coverage radius;
 * otherwise it falls back to basket space.
 */
export const StorageSystem = {
  /** Operational granaries only (under-construction ones don't store yet). */
  granaries(state: SimState): { c: number; r: number; tile: Tile }[] {
    return PlotSystem.buildingsOfType(state, 'granary')
      .filter(g => ConstructionSystem.isOperational(g.tile));
  },

  /** Granaries whose coverage radius reaches the given tile. */
  granariesCovering(state: SimState, c: number, r: number) {
    return this.granaries(state).filter(g =>
      PlotSystem.dist(g.c, g.r, c, r) <= GRANARY_COVERAGE_RADIUS
    );
  },

  capacity(state: SimState): number {
    return BASKET_CAP + this.granaries(state).length * GRANARY_CAP;
  },

  totalUsed(state: SimState): number {
    return (state.res.basket ?? 0) + (state.res.corn ?? 0) + (state.res.carrot ?? 0);
  },

  remaining(state: SimState): number {
    return Math.max(0, this.capacity(state) - this.totalUsed(state));
  },

  deposit(
    state: SimState, c: number, r: number, type: SeedType,
  ): { ok: boolean; toGranary: boolean; granaryC?: number; granaryR?: number } {
    if (this.remaining(state) <= 0) return { ok: false, toGranary: false };
    // Any operational granary raises the global usable cap — coverage radius
    // only chooses WHICH granary the visual harvest hint flies toward, never
    // whether the deposit is allowed.
    const nearby = this.granariesCovering(state, c, r);
    const anyGranary = this.granaries(state).length > 0;
    const target = nearby[0] ?? this.granaries(state)[0];
    if (type === 'corn') state.res.corn++;
    else if (type === 'carrot') state.res.carrot++;
    else state.res.basket++;
    return {
      ok: true,
      toGranary: anyGranary,
      granaryC: target?.c,
      granaryR: target?.r,
    };
  },

  report(state: SimState): StorageReport {
    const cap = this.capacity(state);
    const used = this.totalUsed(state);
    const granCap = cap - BASKET_CAP;
    const basketUsed = Math.min(BASKET_CAP, used);
    return {
      basketUsed,
      basketCap: BASKET_CAP,
      granaryUsed: Math.max(0, used - BASKET_CAP),
      granaryCap: granCap,
      totalUsed: used,
      totalCap: cap,
      byCrop: {
        wheat:  state.res.basket ?? 0,
        corn:   state.res.corn ?? 0,
        carrot: state.res.carrot ?? 0,
      },
    };
  },
};
